import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, from, of } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, delay, filter, map, switchMap, toArray } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { SseService } from './SseService';
import { OAuthService } from './oauth.service';
import { BrokerAdapterFactory } from './broker-adapter-factory.service';
import { ColaOrdenesService } from './cola-ordenes.service';
import { motivoRechazoMetaApi, translateMetaApiHttpError } from './meta-api-error-messages';
import { ConfirmDialogComponent } from '../components/common/confirm-dialog/confirm-dialog.component';
import { EditarOrdenComponent } from '../components/broker/editar-orden/editar-orden.component';

/** Estados en los que una orden ya no se puede cancelar ni editar en el broker. */
const ESTADOS_CERRADOS = ['Filled', 'Canceled', 'Cancelled', 'Inactive', 'Sincro'];

/**
 * Cancelar / editar una orden pendiente del broker (IBKR o MetaAPI) o de la cola de envío.
 * Misma lógica que lista-ordenes (CancelarOrden / editarOrden), para la tabla de operaciones estilo MT.
 */
@Injectable({
  providedIn: 'root'
})
export class AccionesOrdenBrokerService {

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
    private cookieService: CookieService,
    private sseService: SseService,
    private oauthService: OAuthService,
    private brokerAdapterFactory: BrokerAdapterFactory,
    private colaOrdenesService: ColaOrdenesService
  ) { }

  esCerrada(orden: any): boolean {
    return ESTADOS_CERRADOS.includes(orden?.status);
  }

  /**
   * Pide confirmación y cancela la orden. Emite `true` cuando el broker aceptó la cancelación
   * (el llamador debe recargar la lista); no emite si el usuario no confirma o no se puede operar.
   * Las órdenes en cola solo se retiran de la cola: aún no existen en el broker.
   */
  cancelarOrden(orden: any, wallet: any, panelClass: string): Observable<boolean> {
    if (orden?.enCola) {
      return this.confirmar('Esta Seguro de Cancelar la orden en cola de ' + orden.ticker, panelClass).pipe(
        map(() => {
          this.colaOrdenesService.solicitarCancelacion(orden.cOID);
          return false;
        })
      );
    }
    if (this.esCerrada(orden)) {
      this.avisar('No se Pueden Cancelar Ordenes en estado : ' + orden.status, panelClass);
      return of();
    }
    if (orden?.orderId == null) {
      this.avisar('Esta orden no se puede cancelar, se debe eliminar', panelClass);
      return of();
    }
    // MetaTrader con Algo Trading desactivado: aviso y no se inicia el flujo
    return this.brokerAdapterFactory.puedeOperar(wallet?.tipoBroker).pipe(
      filter(ok => !!ok),
      switchMap(() => this.confirmar('Esta Seguro de Cancelar la Orden ' + orden.orderId, panelClass)),
      switchMap(() => wallet?.tipoBroker === 'METAAPI'
        ? this.cancelarMetaApi(orden, wallet)
        : this.cancelarIbkr(orden, wallet))
    );
  }

  /**
   * Cancela todas las órdenes pendientes recibidas tras una sola confirmación, una tras otra (como
   * lista-ordenes). Emite al terminar; un fallo no corta la serie. Las de la cola solo se retiran de ella.
   */
  cancelarTodas(ordenes: any[], wallet: any, panelClass: string): Observable<void> {
    const cancelables = (ordenes || []).filter(o => o.enCola || (!this.esCerrada(o) && o.orderId != null));
    if (!cancelables.length) {
      this.toastr.info('No hay órdenes pendientes que cancelar', 'Órdenes');
      return of();
    }
    const pausa = wallet?.tipoBroker === 'METAAPI' ? 500 : 1000;
    return this.brokerAdapterFactory.puedeOperar(wallet?.tipoBroker).pipe(
      filter(ok => !!ok),
      switchMap(() => this.confirmar('Esta Seguro de Cancelar las ' + cancelables.length + ' ordenes pendientes', panelClass)),
      switchMap(() => from(cancelables).pipe(
        concatMap(orden => {
          if (orden.enCola) {
            this.colaOrdenesService.solicitarCancelacion(orden.cOID);
            return of(false);
          }
          const cancelar$ = wallet?.tipoBroker === 'METAAPI' ? this.cancelarMetaApi(orden, wallet) : this.cancelarIbkr(orden, wallet);
          return cancelar$.pipe(defaultIfEmpty(false), catchError(() => of(false)), delay(pausa));
        }),
        toArray(),
        map(() => undefined)
      ))
    );
  }

  /** Borra de la BD una orden cerrada o nunca enviada (sin estado). Emite `true` si se borró. */
  eliminarOrden(orden: any, panelClass: string): Observable<boolean> {
    if (orden?.enCola) {
      this.toastr.info('Orden en cola, aún no enviada al broker', 'Órdenes');
      return of();
    }
    if (orden?.status && !this.esCerrada(orden)) {
      this.toastr.error('No se puede eliminar la orden con estado: ' + orden.status + ' cancele la orden si la tiene pendiente.',
        'Error', { timeOut: 10000, positionClass: 'toast-top-center' });
      return of();
    }
    return this.confirmar('Esta Seguro de Eliminar la Orden ' + (orden.orderId || orden.idorden), panelClass).pipe(
      switchMap(() => this.sseService.apiDelete('broker/eliminarOrden/' + orden.idorden, this.cookieService.get('auth_token'))),
      map(() => true)
    );
  }

  /** Borra de la BD varias órdenes cerradas o sin enviar (las de una posición) tras una sola confirmación. */
  eliminarOrdenes(ordenes: any[], nombre: string, panelClass: string): Observable<boolean> {
    if (ordenes.length === 1) return this.eliminarOrden(ordenes[0], panelClass);
    if (ordenes.some(o => o?.enCola || (o?.status && !this.esCerrada(o)))) {
      this.toastr.error('La operación tiene órdenes pendientes: cancélelas antes de eliminarla.',
        'Error', { timeOut: 10000, positionClass: 'toast-top-center' });
      return of();
    }
    const licencia = this.cookieService.get('auth_token');
    return this.confirmar('Esta Seguro de Eliminar las ' + ordenes.length + ' ordenes de ' + nombre, panelClass).pipe(
      switchMap(() => from(ordenes).pipe(
        concatMap(o => this.sseService.apiDelete('broker/eliminarOrden/' + o.idorden, licencia).pipe(catchError(() => of(null)))),
        toArray()
      )),
      map(() => true)
    );
  }

  /** Abre el diálogo de edición; las órdenes en cola o ya cerradas no se editan. `extra` se añade a su data. */
  editarOrden(orden: any, wallet: any, panelClass: string, extra: any = {}): void {
    if (orden?.enCola) {
      this.toastr.info('Orden en cola, aún no enviada al broker', 'Órdenes');
      return;
    }
    if (this.esCerrada(orden)) {
      this.avisar('No se Pueden Editar Ordenes en estado ' + orden.status, panelClass);
      return;
    }
    this.dialog.open(EditarOrdenComponent, {
      width: '400px',
      maxHeight: '90vh',
      panelClass,
      data: {
        orden,
        wallet,
        tipoBroker: wallet?.tipoBroker ?? 'IBKR',
        metaApiAccountId: wallet?.metaApiAccountId ?? '',
        ...extra
      }
    });
  }

  private cancelarMetaApi(orden: any, wallet: any): Observable<boolean> {
    const accountId = wallet.metaApiAccountId || wallet.nombre;
    const licencia = this.cookieService.get('auth_token');
    return new Observable<boolean>(subscriber => {
      this.brokerAdapterFactory.getAdapter(wallet).cancelOrder(accountId, String(orden.orderId)).subscribe({
        next: (res: any) => {
          const rechazo = motivoRechazoMetaApi(res);
          if (rechazo) {
            this.toastr.error('MetaAPI error: ' + rechazo, 'MetaAPI', { timeOut: 30000 });
            subscriber.complete();
            return;
          }
          this.sseService.apiPostNew('metaapi/cancelarOrden', { order_id: Number(orden.orderId) }, licencia).subscribe({
            error: (error: any) => console.error(`Error al cancelar orden ${orden.orderId} en MetaAPI:`, error)
          });
          subscriber.next(true);
          subscriber.complete();
        },
        error: (err: any) => {
          console.error('MetaAPI cancel order error', err);
          const msg = err?.error?.message ?? err?.message ?? 'unknown';
          this.toastr.error('MetaAPI error: ' + translateMetaApiHttpError(err?.error?.error, msg), 'MetaAPI', { timeOut: 30000 });
          subscriber.complete();
        }
      });
    });
  }

  /** DELETE en IBKR y luego se consulta el estado para guardarlo en BD. */
  private cancelarIbkr(orden: any, wallet: any): Observable<boolean> {
    const licencia = this.cookieService.get('auth_token');
    return this.oauthService.apiDeleteIbrk('/iserver/account/' + wallet.nombre + '/order/' + orden.orderId).pipe(
      switchMap(() => this.oauthService.apiGetIbrk('/iserver/account/order/status/' + orden.orderId)),
      switchMap((estado: any) => this.sseService.apiPostNew('broker/actualizarOrdenEstado', estado, licencia)),
      map(() => true)
    );
  }

  private confirmar(msj: string, panelClass: string): Observable<true> {
    return this.dialog.open(ConfirmDialogComponent, {
      width: '380px', height: '250px', panelClass, data: { msj }
    }).afterClosed().pipe(filter(ok => !!ok), map(() => true as const));
  }

  private avisar(msj: string, panelClass: string): void {
    this.dialog.open(ConfirmDialogComponent, { width: '380px', height: '250px', panelClass, data: { msj } });
  }
}
